import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, FileText, Download, Image, File, ExternalLink } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AffiliateCaseDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { afiliatToken, afiliatData, adminToken } = useAuth();
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  
  const isAdmin = afiliatData ? afiliatData.is_admin : false;
  // Use adminToken if available (for admin logged in affiliate portal), otherwise use afiliatToken
  const authToken = adminToken || afiliatToken;

  useEffect(() => {
    if (!afiliatToken) {
      navigate("/affiliate/login");
      return;
    }
    
    const loadCase = async () => {
      try {
        const response = await fetch(`${API}/affiliate/cases/${id}`, {
          headers: { Authorization: `Bearer ${afiliatToken}` }
        });
        if (!response.ok) throw new Error("Not found");
        const data = await response.json();
        setCaseData(data);
      } catch (err) {
        toast.error("Dosarul nu a fost găsit");
        navigate("/affiliate/cases");
      } finally {
        setLoading(false);
      }
    };
    
    loadCase();
  }, [afiliatToken, navigate, id]);

  const getDocumentUrl = (doc) => {
    // Use the afiliatToken for admin viewing documents (admin is logged in via affiliate portal)
    return `${API}/admin/documents/${doc.id}?token=${afiliatToken}`;
  };

  const isImageFile = (doc) => {
    const ext = (doc.extensie || doc.tip || "").toLowerCase();
    return ext.includes("jpg") || ext.includes("jpeg") || ext.includes("png") || ext.includes("gif") || 
           ext.includes("image");
  };

  const openDocument = async (doc) => {
    if (isImageFile(doc)) {
      setSelectedImage(doc);
    } else {
      // For non-image files, open in new tab
      window.open(getDocumentUrl(doc), "_blank");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!caseData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">Nu s-a găsit dosarul.</p>
      </div>
    );
  }

  const showAdminView = isAdmin && caseData.is_admin;
  const documents = caseData.documente || [];
  const hasDocuments = documents.length > 0;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="case-detail">
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16 gap-4">
            <Link to="/affiliate/cases" className="p-2 hover:bg-gray-100 rounded-lg">
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </Link>
            <span className="font-bold text-gray-900">Detalii Dosar</span>
            {isAdmin && <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded">ADMIN</span>}
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl p-8 border border-gray-100">
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">ID Dosar</p>
                <p className="font-mono text-xl font-bold text-emerald-600">{caseData.numar_dosar}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-gray-500 mb-1">Data</p>
                <p className="text-gray-900">{caseData.data}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Status</p>
                <span className="inline-block px-3 py-1 rounded-full text-sm font-medium bg-emerald-100 text-emerald-700">
                  {caseData.status}
                </span>
              </div>
            </div>

            {showAdminView && (
              <div className="border-t border-gray-100 pt-6">
                <h3 className="font-bold text-gray-900 mb-4">Informații Client</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Nume</p>
                    <p className="text-gray-900 font-medium">{caseData.nume_prenume}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Telefon</p>
                    <p className="text-gray-900 font-medium">{caseData.telefon}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="text-gray-900">{caseData.email || "-"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Oraș</p>
                    <p className="text-gray-900">{caseData.oras || "-"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Tip daună</p>
                    <p className="text-gray-900">{caseData.tip_dauna || "-"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Cod Afiliat</p>
                    <p className="text-gray-900 font-mono text-emerald-600">{caseData.cod_afiliat || "-"}</p>
                  </div>
                </div>
              </div>
            )}

            {showAdminView && caseData.detalii && (
              <div className="border-t border-gray-100 pt-6">
                <p className="text-sm text-gray-500 mb-2">Detalii eveniment</p>
                <p className="text-gray-900 bg-gray-50 p-4 rounded-xl">{caseData.detalii}</p>
              </div>
            )}

            {showAdminView && hasDocuments && (
              <div className="border-t border-gray-100 pt-6">
                <p className="text-sm text-gray-500 mb-3">Documente încărcate ({documents.length})</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {documents.map((doc, i) => (
                    <button
                      key={i}
                      onClick={() => openDocument(doc)}
                      className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl hover:bg-emerald-50 hover:border-emerald-200 transition-colors text-left group border border-gray-100"
                      data-testid={`document-${i}`}
                    >
                      {isImageFile(doc) ? (
                        <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Image className="w-6 h-6 text-blue-600" />
                        </div>
                      ) : (
                        <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <File className="w-6 h-6 text-emerald-600" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900 font-medium truncate">{doc.nume_original}</p>
                        <p className="text-xs text-gray-500">{isImageFile(doc) ? "Click pentru a vizualiza" : "Click pentru a deschide"}</p>
                      </div>
                      <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-emerald-500 flex-shrink-0" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {!showAdminView && (
              <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100">
                <p className="text-emerald-700 text-sm">
                  <strong>Notă:</strong> Dosarul este gestionat de echipa noastră. 
                  Vei fi notificat când statusul se schimbă. Pentru detalii suplimentare, contactează administratorul.
                </p>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Image Preview Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh] w-full">
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 text-lg font-medium"
            >
              Închide ✕
            </button>
            <img
              src={getDocumentUrl(selectedImage)}
              alt={selectedImage.nume_original}
              className="max-w-full max-h-[85vh] mx-auto rounded-lg object-contain"
              onError={(e) => {
                e.target.onerror = null;
                toast.error("Nu s-a putut încărca imaginea");
                setSelectedImage(null);
              }}
            />
            <p className="text-white text-center mt-4 font-medium">{selectedImage.nume_original}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AffiliateCaseDetail;
